/** BFP-252 — instrument publish wizard demographic filters (shared FE/BE). */

import type { AudienceEkycStatusFilter } from './ekyc'
import type { ProfileAttributeFilter } from './profile-attributes'
import type {
  CurrentVoterRegistrationValue,
  RegisteredToVoteValue,
} from './voter-profile'

/** BFP-253 — distribution channel ids persisted on `publishConfig.selectedChannels`. */
export type DistributionChannelId = 'push' | 'email' | 'sms'

/**
 * BFP-1077 — who may receive a published regular survey.
 * BFP-1141 — `all_registered` broadcasts to every active registered user (web + mobile)
 * with no demographic / Senate District / eKYC / party filtering.
 */
export type AudienceAvailability = 'existing_only' | 'existing_and_future' | 'all_registered'

export const AUDIENCE_AVAILABILITY_EXISTING_ONLY = 'existing_only' as const
export const AUDIENCE_AVAILABILITY_EXISTING_AND_FUTURE = 'existing_and_future' as const
export const AUDIENCE_AVAILABILITY_ALL_REGISTERED = 'all_registered' as const

export const AUDIENCE_AVAILABILITY_VALUES = [
  AUDIENCE_AVAILABILITY_EXISTING_ONLY,
  AUDIENCE_AVAILABILITY_EXISTING_AND_FUTURE,
  AUDIENCE_AVAILABILITY_ALL_REGISTERED,
] as const

/** BFP-1120 — future-matching assignment source. `web_survey` skips Independent + eKYC gates. */
export const FUTURE_MATCHING_ASSIGNMENT_SOURCES = ['ekyc', 'signup_survey', 'web_survey'] as const
export type FutureMatchingAssignmentSource =
  (typeof FUTURE_MATCHING_ASSIGNMENT_SOURCES)[number]

/** S2S body for campaign `POST /internal/instruments/assign-future-matching`. */
export interface IAssignFutureMatchingBody {
  platformUserId: string
  senateDistrict: string | null
  registeredPoliticalParty: string | null
  ekycStatus?: string
  assignmentSource?: FutureMatchingAssignmentSource
  /** BFP-1151 — current profile value. Omitted or null does not match a selected filter. */
  registeredToVote?: string | null
  /** BFP-1151 — current profile value. Omitted or null does not match a selected filter. */
  currentVoterRegistration?: string | null
}

export const SENATE_DISTRICT_OPTIONS = ['SD 6', 'SD 16', 'SD 24', 'SD 36', 'SD 40', 'SD 46'] as const
export type SenateDistrictOption = (typeof SENATE_DISTRICT_OPTIONS)[number]

/** Signup + source-audience Independent labels (BFP-1054 / BFP-1077). */
export const INDEPENDENT_POLITICAL_PARTY_VALUES = [
  'Independent',
  'Registered Independent',
] as const

export type DemographicPanelId =
  | 'ageRange'
  | 'gender'
  | 'location'
  | 'education'
  | 'employment'
  | 'deviceType'
  | 'maritalStatus'
  | 'householdSize'
  | 'numberOfChildren'
  | 'industry'
  | 'housingStatus'
  | 'senateDistrict'
  | 'registeredToVote'
  | 'currentVoterRegistration'

/** Distribution channel on a Signal Provider profile (`user_profiles.deviceTypes`). */
export type DeviceChannel = 'mobile' | 'web'

/** Normalizes API / persisted values (legacy string pill or channel array). */
export function normalizeDeviceTypeFilter(value: unknown): DeviceChannel[] | undefined {
  if (value == null) return undefined
  if (typeof value === 'string') {
    if (value === 'both') return ['mobile', 'web']
    if (value === 'mobile' || value === 'web') return [value]
    return undefined
  }
  if (!Array.isArray(value)) return undefined
  const channels = value.filter((v): v is DeviceChannel => v === 'mobile' || v === 'web')
  if (!channels.length) return undefined
  return [...new Set(channels)]
}

export interface AudienceFilters {
  ageRange?: { min?: number; max?: number }
  gender?: string[]
  location?: { state?: string; city?: string }
  education?: string[]
  employment?: string[]
  /** Selected channels — e.g. `['mobile']`, `['web']`, or `['mobile', 'web']` (not `"both"`). */
  deviceType?: DeviceChannel[]
  maritalStatus?: string[]
  householdSize?: string[]
  numberOfChildren?: string[]
  industry?: string[]
  housingStatus?: string[]
  /**
   * BFP-1077 — PA Senate District targeting. Single select from `SENATE_DISTRICT_OPTIONS`.
   */
  senateDistrict?: string
  /**
   * Match-only political-party filter (not a researcher panel).
   * Future-matching publish applies Independent aliases via `applyFutureMatchingHardcodedFilters`.
   */
  registeredPoliticalParty?: string[]
  /**
   * BFP-1151 — Currently Registered to Vote. Multi-select. Blank profiles do not match.
   */
  registeredToVote?: RegisteredToVoteValue[]
  /**
   * BFP-1151 — Current Voter Registration from the Sign-Up survey answer.
   * Not `registeredPoliticalParty`.
   */
  currentVoterRegistration?: CurrentVoterRegistrationValue[]
  /**
   * BFP-511 — eKYC targeting.
   * `verified` = ekycStatus verified only.
   * `unverified` = not_started | unverified | failed | under_review.
   * Omit or `all` = no eKYC filter.
   */
  ekycStatus?: AudienceEkycStatusFilter
}

export interface PublishConfigTargetAudience {
  /** Active audience filters — always used for match count, publish, and notifications. */
  filters: AudienceFilters
  /** Frozen AI baseline — Restore AI Suggestions only; never used as the active filter set. */
  aiSuggestedFilters?: AudienceFilters
  suggestedPanelIds?: DemographicPanelId[]
  /** Panels the user changed away from `aiSuggestedFilters` (persisted for reload). */
  userModifiedPanelIds?: DemographicPanelId[]
  /** When true, `filters` differ from the last applied AI suggestion. */
  filtersModified?: boolean
  /**
   * BFP-252 — true only after the researcher applies AI (Restore AI Suggestions).
   * Missing/false: Age, Device Type, and other AI demographics stay unselected.
   */
  aiSuggestionsApplied?: boolean
  /** Last matching Signal Provider count for the active filters (persisted on Save Draft). */
  count?: number
  /**
   * BFP-1143 — dynamic profile-attribute filters (AND across attributes, OR within values).
   * Legacy `filters` stay for older drafts and Dynamic Survey eKYC / party gates.
   */
  profileAttributeFilters?: ProfileAttributeFilter[]
}

/** Stored documents may still have `aiBaseline` before migration to `aiSuggestedFilters`. */
export type PublishConfigTargetAudienceStored = PublishConfigTargetAudience & {
  aiBaseline?: AudienceFilters
}

/** Reads `aiSuggestedFilters`, falling back to legacy `aiBaseline` in stored documents. */
export function readAiSuggestedFilters(
  targetAudience: PublishConfigTargetAudience | PublishConfigTargetAudienceStored | undefined | null,
): AudienceFilters | undefined {
  if (!targetAudience) return undefined
  const stored = targetAudience as PublishConfigTargetAudienceStored
  return targetAudience.aiSuggestedFilters ?? stored.aiBaseline
}

/**
 * Stored active filters (`targetAudience.filters` only).
 * Use `readAiSuggestedFilters` only for Restore AI — not for match/publish.
 */
export function activeAudienceFiltersFromTargetAudience(
  targetAudience: PublishConfigTargetAudience | PublishConfigTargetAudienceStored,
): AudienceFilters {
  return targetAudience.filters ?? {}
}

/** Deep copy for plain JSON-shaped filters. */
function cloneAudienceFilters(filters: AudienceFilters): AudienceFilters {
  return structuredClone(filters)
}

/**
 * Canonical shape written to Mongo on Save Draft / publish.
 * BFP-252 — never copy `aiSuggestedFilters` into active `filters`.
 */
export function canonicalizeTargetAudienceForStorage(
  targetAudience: PublishConfigTargetAudience,
): PublishConfigTargetAudience {
  const aiSuggested = readAiSuggestedFilters(targetAudience) ?? {}
  const userFilters = cloneAudienceFilters(targetAudience.filters ?? {})
  return {
    ...targetAudience,
    filters: userFilters,
    aiSuggestedFilters:
      Object.keys(aiSuggested).length > 0
        ? cloneAudienceFilters(aiSuggested)
        : targetAudience.aiSuggestedFilters,
    filtersModified: targetAudience.filtersModified === true,
    aiSuggestionsApplied: targetAudience.aiSuggestionsApplied === true ? true : undefined,
  }
}

export function isSenateDistrictOption(value: unknown): value is SenateDistrictOption {
  return (
    typeof value === 'string' &&
    (SENATE_DISTRICT_OPTIONS as readonly string[]).includes(value)
  )
}

export function readAudienceAvailability(
  config: { audienceAvailability?: AudienceAvailability } | null | undefined,
): AudienceAvailability {
  const value = config?.audienceAvailability
  if (value === AUDIENCE_AVAILABILITY_EXISTING_AND_FUTURE) return value
  if (value === AUDIENCE_AVAILABILITY_ALL_REGISTERED) return value
  return AUDIENCE_AVAILABILITY_EXISTING_ONLY
}

/** BFP-1144 — SMS invite copy (plain text). */
export interface PublishSmsContent {
  message: string
}

/** BFP-1144 — Email invite copy; `bodyHtml` is rich text (sanitized at send time). */
export interface PublishEmailContent {
  subject: string
  bodyHtml: string
}

/** BFP-1144 — Push / bell invite copy (plain text). */
export interface PublishPushContent {
  title: string
  message: string
}

/** BFP-1144 — per-channel invite copy on `publishConfig.channelContent`. */
export interface PublishChannelContent {
  sms?: PublishSmsContent
  email?: PublishEmailContent
  push?: PublishPushContent
}

/** BFP-1144 — max lengths, counted on plain text (HTML markup excluded). */
export const CHANNEL_CONTENT_LIMITS = {
  smsMessage: 250,
  emailSubject: 100,
  emailBody: 250,
  pushTitle: 50,
  pushMessage: 250,
} as const

/** Instrument publish flow persisted on `instruments.publishConfig` (BFP-252–256).
 * BFP-1011 Sign-up Surveys may preset push-only channels, `rewardEnabled`,
 * `budgetConfigured`, and `rewardPerResponse` (≥ 0.10) with no `responseTarget`.
 */
export interface PublishConfigDraft {
  wizardStep: number
  maxReachedStep: number
  targetAudience?: PublishConfigTargetAudience
  selectedChannels?: DistributionChannelId[]
  /** BFP-1144 — independently customized copy per distribution channel. */
  channelContent?: PublishChannelContent
  /**
   * Legacy BFP-598 global title (max 50). Read-only fallback for drafts saved before
   * BFP-1144 `channelContent`; new writes use `channelContent`.
   */
  notificationTitle?: string
  /** Legacy BFP-598 global body (max 100). Read-only fallback — see `notificationTitle`. */
  notificationBody?: string
  /** When true, instrument offers rewards (Step 4 budget applies). */
  rewardEnabled?: boolean
  budgetConfigured?: boolean
  /** BFP-618 — standard (min 800) vs scientific (formula-derived, may be &lt; 800). */
  targetingMode?: import('./scientific-sample-size').ResponseTargetingMode
  /** BFP-618 — scientific only. */
  confidenceLevel?: import('./scientific-sample-size').ScientificConfidenceLevel
  /** BFP-618 — scientific only; integer percent 1–10. */
  marginOfErrorPercent?: number
  /** BFP-618 — true after "Use This Sample Size" locks the formula-derived target. */
  scientificTargetLocked?: boolean
  /** BFP-618 — Step 4 UI phase persisted for draft resume (mode selection vs tabs vs standalone scientific). */
  step4Phase?: 'mode_selection' | 'standard_tabs' | 'scientific'
  /**
   * BFP-1078 — optional for `existing_and_future` (null/omitted = unlimited, no auto-close).
   * Required and positive for `existing_only`. Scientific targeting remains forbidden in future mode.
   */
  responseTarget?: number | null
  rewardPerResponse?: number | null
  activeBudgetTab?: string
  budgetTabConfirmations?: Record<string, boolean>
  /**
   * BFP-1077 — default `existing_only` when omitted (legacy drafts).
   * `existing_and_future` auto-assigns matching Independents after eKYC verified.
   * BFP-1141 — `all_registered` ignores `targetAudience.filters` and publishes to all active users.
   */
  audienceAvailability?: AudienceAvailability
}

export interface AiAudienceSuggestResponse {
  /** Active working filters (may differ from AI after user edits). */
  filters: AudienceFilters
  /** Frozen first AI suggestion — for Restore and badge comparison. */
  aiSuggestedFilters: AudienceFilters
  suggestedPanelIds: DemographicPanelId[]
  /** Panels user changed away from AI (optional; FE may recompute if absent). */
  userModifiedPanelIds?: DemographicPanelId[]
}

export type AudienceMatchPoolStatus = 'empty' | 'live'

export interface AudienceMatchResponse {
  count: number
  poolStatus: AudienceMatchPoolStatus
  message?: string
}
